import type { TriggerSchedule } from '@/types/custom-playbooks';

/**
 * The day a monthly schedule runs on, the way Clu's `set_schedule` takes it:
 * a day from the 1st to the 30th, 31 for the month's last day, or the first
 * business day. One vocabulary for the Day control and the words, so the
 * builder, the agent panel and Clu name a day the same way. The next run
 * itself always comes from the server; the browser never works out a date.
 */

/** `dayOfMonth` 31 reads as the month's last day. */
export const LAST_DAY_OF_MONTH = 31;

/** The Day control's value for the first business day. */
export const FIRST_BUSINESS_DAY = 'first_business_day';

/** 1 as "1st", 2 as "2nd", 11 as "11th", 22 as "22nd". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return n + 'th';
  switch (n % 10) {
    case 1:
      return n + 'st';
    case 2:
      return n + 'nd';
    case 3:
      return n + 'rd';
    default:
      return n + 'th';
  }
}

/** The Day control's options: 1st to 30th, Last day, First business day. */
export const MONTH_DAY_OPTIONS: readonly { value: string; label: string }[] = [
  ...Array.from({ length: 30 }, (_, index) => ({
    value: String(index + 1),
    label: ordinal(index + 1),
  })),
  { value: String(LAST_DAY_OF_MONTH), label: 'Last day' },
  { value: FIRST_BUSINESS_DAY, label: 'First business day' },
];

/**
 * The Day control's value for a monthly schedule: blank unless it holds
 * exactly one valid day field, the rule `validateScheduleConfig` checks. A
 * blank value shows the control's placeholder, so whichever day is picked is
 * a change the select reports and writes.
 */
export function monthDayValue(
  schedule: Pick<TriggerSchedule, 'dayOfMonth' | 'firstBusinessDay'>
): string {
  const day = schedule.dayOfMonth;
  const hasDay = day !== undefined && day !== null;
  if (schedule.firstBusinessDay === true)
    return hasDay ? '' : FIRST_BUSINESS_DAY;
  const valid =
    typeof day === 'number' &&
    Number.isInteger(day) &&
    day >= 1 &&
    day <= LAST_DAY_OF_MONTH;
  return valid ? String(day) : '';
}

/**
 * The schedule fields a Day control value writes: exactly one of
 * `dayOfMonth` and `firstBusinessDay`, the other removed.
 */
export function monthDayFields(
  value: string
): Pick<TriggerSchedule, 'dayOfMonth' | 'firstBusinessDay'> {
  if (value === FIRST_BUSINESS_DAY)
    return { dayOfMonth: undefined, firstBusinessDay: true };
  const day = Math.round(Number(value));
  return {
    dayOfMonth: Number.isFinite(day)
      ? Math.min(LAST_DAY_OF_MONTH, Math.max(1, day))
      : 1,
    firstBusinessDay: undefined,
  };
}
